// 他参加者がテキスト / 付箋を編集中のキャレット位置を表示するレイヤ（エピックボード相当）。
//
// transient store（useCanvasTransientStore）の textCarets を購読し、各キャレットについて対象オブジェクトの
// エディタ DOM（data-testid="canvas-object-text-<id>"）から cursorIndex 文字目の位置を Range で実測して
// ステージ座標へ変換し、色付きの縦バー＋名前ラベルで描画する。自分自身のキャレットは描画しない。
//
// 制約:
// - 回転しているオブジェクトはキャレット位置計算が不正確になるため描画しない（best-effort）。
// - 送信側は生 Markdown テキスト基準（改行込み）の文字オフセットを送る。受信側は表示 DOM が
//   Markdown 整形されている（マーカーが隠れる / グリフが付く）ため、生オフセットをそのまま
//   テキストノードへ当てると見出し・箇条書きでずれる。これを避けるため mapRawOffsetToDisplay で
//   「行 index + 行内オフセット」へ写像し、対象行の <div> 内だけを走査して実測する。
//   受信側 DOM が編集中（WYSIWYG）か非編集（MarkdownTextView）かで写像規則が異なるため、
//   [data-md] ブロックの有無で surface を判定する。

import { useEffect, useLayoutEffect, useState } from "react";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import { useCanvasTransientStore } from "./store/canvas-transient-store";
import { screenToStage } from "../../lib/canvas-coordinates";
import {
  mapRawOffsetToDisplay,
  resolveCaretTextAnchor,
  type CaretSurface,
} from "../../lib/markdown-wysiwyg";

interface Props {
  readonly currentUserId: string;
}

// 一定時間更新が無いキャレットは破棄する（編集終了 = ブロードキャスト停止から自動的に消えるようにする）。
const STALE_MS = 1500;
const CARET_LAYER_Z_INDEX = Number.MAX_SAFE_INTEGER - 3;

interface CaretPosition {
  readonly stageX: number;
  readonly stageY: number;
  /** キャレットの高さ（ステージ座標 px = 行高）。 */
  readonly stageHeight: number;
  readonly color: string;
  readonly displayName: string;
}

export function CanvasRemoteCaretsLayer({ currentUserId }: Props): JSX.Element {
  const textCarets = useCanvasTransientStore((s) => s.textCarets);
  const prune = useCanvasTransientStore((s) => s.prune);
  const objects = useCanvasDocStore((s) => s.objects);
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const [positions, setPositions] = useState<Record<string, CaretPosition>>({});

  // 編集停止後に残るキャレットを定期的に掃除する（プレビューゴーストと同じ間隔）。
  useEffect(() => {
    const id = window.setInterval(() => prune(STALE_MS), 1000);
    return () => window.clearInterval(id);
  }, [prune]);

  // textCarets / objects / viewers の変化時にエディタ DOM を実測して位置を求める。
  // pan / zoom では位置（ステージ座標）は不変のため、view は依存に含めず getState() で都度読む。
  useLayoutEffect(() => {
    const view = useCanvasStore.getState().view;
    const now = Date.now();
    const next: Record<string, CaretPosition> = {};
    for (const [key, caret] of Object.entries(textCarets)) {
      if (caret.userId === currentUserId) continue;
      if (now - caret.updatedAt > STALE_MS) continue;
      const object = objects[caret.objectId];
      if (!object) continue;
      if (object.type !== "text" && object.type !== "sticky") continue;
      // 回転オブジェクトはキャレット位置が不正確になるため描画しない。
      if (object.rotation) continue;
      const el = document.querySelector<HTMLElement>(
        `[data-testid="canvas-object-text-${caret.objectId}"]`
      );
      if (!el) continue;
      // 生オフセット（cursorIndex）を、表示 DOM の「行 index + 行内オフセット」へ写像する。
      // surface: 編集中 WYSIWYG DOM（[data-md] ブロックあり）は "editor"、非編集表示は "view"。
      const surface: CaretSurface = el.querySelector("[data-md]") ? "editor" : "view";
      const display = mapRawOffsetToDisplay(object.text, caret.cursorIndex, surface);
      // 対象行の <div> 内だけを走査する。行要素が取れない場合は従来どおり全体を走査する（フォールバック）。
      const lineEl = el.children[display.lineIndex];
      const target = lineEl instanceof HTMLElement ? lineEl : el;
      const offset = lineEl instanceof HTMLElement ? display.lineOffset : caret.cursorIndex;
      const rect = measureCaretClientRect(target, offset);
      if (!rect) continue;
      const stage = screenToStage({ x: rect.left, y: rect.top }, view);
      const viewer = viewers[caret.userId];
      next[key] = {
        stageX: stage.x,
        stageY: stage.y,
        stageHeight: rect.height / view.zoom,
        color: normalizeColor(viewer?.color),
        displayName: viewer?.displayName ?? "",
      };
    }
    // 依存に positions を含めないため、この setState でこの effect は再実行されない（ループしない）。
    setPositions(next);
  }, [textCarets, objects, viewers, currentUserId]);

  return (
    <div
      data-testid="canvas-remote-carets-layer"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: CARET_LAYER_Z_INDEX,
      }}
    >
      {Object.entries(positions).map(([key, pos]) => (
        <div
          key={key}
          data-testid="remote-text-caret"
          style={{
            position: "absolute",
            left: `${pos.stageX}px`,
            top: `${pos.stageY}px`,
            height: `${pos.stageHeight}px`,
            // バー幅はズーム非依存で常に約 2px に見せる（選択 UI と同じ補正）。
            width: "calc(2px * var(--canvas-inv-zoom, 1))",
            background: pos.color,
            pointerEvents: "none",
          }}
        >
          {pos.displayName ? (
            <div
              style={{
                position: "absolute",
                left: 0,
                bottom: "100%",
                // ラベルもズーム非依存サイズにする。
                transform: "scale(var(--canvas-inv-zoom, 1))",
                transformOrigin: "left bottom",
                padding: "1px 6px",
                marginBottom: "2px",
                background: pos.color,
                color: "#ffffff",
                fontSize: "11px",
                fontWeight: 700,
                borderRadius: "4px",
                whiteSpace: "nowrap",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              }}
            >
              {pos.displayName}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * エディタ要素 el 内で、本文先頭から cursorIndex 文字目のキャレット位置（client 座標）を実測する。
 * テキストノードを走査して cursorIndex に対応する位置へ collapsed Range を作り、その矩形を返す。
 */
function measureCaretClientRect(
  el: HTMLElement,
  cursorIndex: number
): { left: number; top: number; height: number } | null {
  const range = document.createRange();
  // 本文テキストノード上の位置を解決する。編集中箇条書きの編集不可マーカー span（`• `）は
  // 表示専用で本文オフセットに含めないため resolveCaretTextAnchor 側で除外される。
  const anchor = resolveCaretTextAnchor(el, cursorIndex);
  if (!anchor) {
    // テキストノードなし（空）→ 要素先頭。
    range.selectNodeContents(el);
    range.collapse(true);
  } else {
    range.setStart(anchor.node, anchor.offset);
    range.collapse(true);
  }
  const rect = range.getBoundingClientRect();
  if (rect && rect.height > 0) {
    return { left: rect.left, top: rect.top, height: rect.height };
  }
  // collapsed Range の矩形が取れない環境向けフォールバック（要素の rect と line-height で概算）。
  const elRect = el.getBoundingClientRect();
  if (elRect.width === 0 && elRect.height === 0) return null;
  const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
  const height = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : elRect.height || 16;
  return {
    left: rect?.left && rect.left > 0 ? rect.left : elRect.left,
    top: rect?.top && rect.top > 0 ? rect.top : elRect.top,
    height,
  };
}

function normalizeColor(color: string | undefined): string {
  return color && color.trim().length > 0 ? color : "#1e63d4";
}
